import { alpha } from '@mui/material/styles';

/**
 * Material UI (`@mui/material`) Chip `variant="soft"` only.
 * Whistleblower portal (`@whistleblower`) uses Joy (`@mui/joy`) which has its own native `variant="soft"`.
 */
/** Palette keys supported by MUI Chip `color` with variant `soft`. */
export const CHIP_SOFT_COLORS = [
  'default',
  'primary',
  'secondary',
  'success',
  'error',
  'info',
  'warning',
];

/** Shared sizing for all soft chips (status/metadata, not filter toggles). */
export const chipSoftBaseStyle = {
  height: 22,
  fontSize: '0.6875rem',
  fontWeight: 600,
  borderRadius: 999,
  maxWidth: '100%',
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
  },
};

/**
 * @param {import('@mui/material/styles').Theme} theme
 * @param {string} color
 */
export function chipSoftColorStyle(theme, color) {
  if (color === 'default') {
    return {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.secondary,
      border: '1px solid transparent',
      '& .MuiChip-icon': {
        color: theme.palette.text.secondary,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    };
  }

  const paletteColor = theme.palette[color];
  if (!paletteColor?.main) {
    return {};
  }

  const { main, dark } = paletteColor;
  const text = dark || main;

  return {
    backgroundColor: alpha(main, 0.12),
    color: text,
    border: '1px solid transparent',
    '& .MuiChip-icon': {
      color: text,
    },
    '&:hover': {
      backgroundColor: alpha(main, 0.18),
    },
  };
}

/** MUI theme `variants` entries for `<Chip variant="soft" color="…" />`. */
export function createChipSoftVariants() {
  return CHIP_SOFT_COLORS.map(color => ({
    props: { variant: 'soft', color },
    style: ({ theme }) => chipSoftColorStyle(theme, color),
  }));
}

/**
 * Applied via `styleOverrides.root` so soft chips win over MUI's default `color` fill.
 * @param {import('@mui/material/styles').Theme} theme
 * @param {{ variant?: string, color?: string }} ownerState
 */
export function softChipRootOverride(theme, ownerState) {
  if (ownerState?.variant !== 'soft') {
    return {};
  }
  const color = ownerState.color || 'default';
  return {
    ...chipSoftBaseStyle,
    ...chipSoftColorStyle(theme, color),
  };
}
